import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { listSyncRuns, resumeYoutubeSync, runYoutubeSyncBatch } from "@/lib/admin.functions";
import { summarizeDiscards, AUDIT_STOP_LABELS, type AuditStopCode } from "@/lib/sync-audit";
import { Button } from "@/components/ui/button";

import { AlertCircle, CheckCircle2, PlayCircle, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { formatDateTimeBR, formatTimeBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/sync-status")({
  head: () => ({
    meta: [{ title: "Status de Sincronizações · Admin" }, { name: "robots", content: "noindex" }],
  }),
  component: SyncStatusPage,
});

type RunStatus = "running" | "success" | "partial" | "failed";

function SyncStatusPage() {
  const listFn = useServerFn(listSyncRuns);
  const resumeFn = useServerFn(resumeYoutubeSync);
  const batchFn = useServerFn(runYoutubeSyncBatch);
  const queryClient = useQueryClient();
  const [resumingId, setResumingId] = useState<string | null>(null);
  const abortRef = useRef(false);

  const historyQuery = useQuery({
    queryKey: ["sync-runs"],
    queryFn: () => listFn(),
    refetchInterval: 3000,
  });

  const resumeMutation = useMutation({
    mutationFn: async (runId: string) => {
      setResumingId(runId);
      abortRef.current = false;
      const { runId: id } = await resumeFn({ data: { runId } });
      queryClient.invalidateQueries({ queryKey: ["sync-runs"] });
      let done = false;
      let batches = 0;
      while (!done && !abortRef.current) {
        const res = await batchFn({ data: { runId: id } });
        done = res.done;
        batches++;
        queryClient.invalidateQueries({ queryKey: ["sync-runs"] });
        if (batches > 250) break;
      }
    },
    onSuccess: () => {
      setResumingId(null);
      toast.success("Sincronização retomada e concluída.");
    },
    onError: (e: any) => {
      setResumingId(null);
      toast.error(e?.message ?? "Falha ao retomar.");
    },
  });

  const runs = historyQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-primary">
            Status de Sincronizações
          </h1>
          <p className="mt-1 text-muted-foreground">
            Acompanhe cada execução em tempo real. Runs com falha podem ser retomadas do último
            ponto salvo.
          </p>
        </div>
        <Link to="/admin/sincronizar">
          <Button variant="outline">
            <PlayCircle className="mr-2 h-4 w-4" /> Nova sincronização
          </Button>
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="hidden grid-cols-[1.4fr_1fr_1fr_1.6fr_1.4fr] gap-3 border-b border-border bg-muted/30 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:grid">
          <div>Início</div>
          <div>Status</div>
          <div>Lote / páginas</div>
          <div>Progresso</div>
          <div className="text-right">Ações</div>
        </div>

        {historyQuery.isLoading ? (
          <div className="min-h-[60vh] p-8 text-center text-sm text-muted-foreground">
            Carregando…
          </div>
        ) : runs.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Nenhuma sincronização registrada ainda.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {runs.map((r: any) => {
              const pct =
                r.total_videos > 0
                  ? Math.round((r.processed_count / r.total_videos) * 100)
                  : r.status === "running"
                    ? 5
                    : 100;
              const canResume = r.status === "failed" && !!r.next_page_token;
              return (
                <li
                  key={r.id}
                  className="grid gap-3 px-4 py-4 md:grid-cols-[1.4fr_1fr_1fr_1.6fr_1.4fr] md:items-center"
                >
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {formatDateTime(r.started_at)}
                    </div>
                    {r.finished_at && (
                      <div className="text-xs text-muted-foreground">
                        Fim: {formatTime(r.finished_at)} ·{" "}
                        {formatDuration(r.started_at, r.finished_at)}
                      </div>
                    )}
                  </div>

                  <StatusBadge status={r.status} />

                  <div className="text-sm">
                    <div className="font-medium text-foreground">
                      {r.pages_fetched ?? 0}/{r.max_pages ?? "?"} páginas
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.total_videos ?? 0} vídeo(s)
                      {r.next_page_token ? " · há próxima página" : ""}
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {r.processed_count ?? 0}/{r.total_videos ?? 0}
                      </span>
                      <span>{pct}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                      <div
                        className={
                          "h-full transition-all " +
                          (r.status === "failed"
                            ? "bg-destructive"
                            : r.status === "partial"
                              ? "bg-gold"
                              : "bg-primary")
                        }
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      <span>+{r.inserted_count ?? 0} novos</span>
                      <span>~{r.updated_count ?? 0} atual.</span>
                      <span>−{r.skipped_count ?? 0} pulados</span>
                      <span className="text-destructive">×{r.failed_count ?? 0} falhas</span>
                    </div>
                    {r.audit_stop_reason && (
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        Auditoria:{" "}
                        {AUDIT_STOP_LABELS[r.audit_stop_reason as AuditStopCode] ??
                          r.audit_stop_reason}
                      </div>
                    )}
                    {(() => {
                      const d = summarizeDiscards(r.discard_stats);
                      if (d.total === 0) return null;
                      return (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {d.rows.slice(0, 4).map((row) => (
                            <span
                              key={row.reason}
                              title={row.hint}
                              className="rounded-full border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground"
                            >
                              {row.label}: {row.total}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    {r.error_message && (
                      <div
                        className="mt-1 line-clamp-2 text-xs text-destructive"
                        title={r.error_message}
                      >
                        {r.error_message}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2">
                    {canResume && (
                      <Button
                        size="sm"
                        onClick={() => resumeMutation.mutate(r.id)}
                        disabled={resumingId === r.id || resumeMutation.isPending}
                      >
                        <RefreshCw
                          className={`mr-1.5 h-3.5 w-3.5 ${resumingId === r.id ? "animate-spin" : ""}`}
                        />
                        {resumingId === r.id ? "Retomando…" : "Retomar"}
                      </Button>
                    )}
                    {r.status === "failed" && !r.next_page_token && (
                      <span className="text-xs text-muted-foreground">Sem ponto de retomada</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: RunStatus }) {
  const map = {
    running: {
      icon: RefreshCw,
      label: "Em andamento",
      cls: "text-primary bg-primary/10",
      spin: true,
    },
    success: { icon: CheckCircle2, label: "Concluído", cls: "text-primary bg-primary/10" },
    partial: { icon: AlertCircle, label: "Com falhas", cls: "text-gold bg-gold/10" },
    failed: { icon: XCircle, label: "Falhou", cls: "text-destructive bg-destructive/10" },
  } as const;
  const it = map[status] ?? map.failed;
  const Icon = it.icon;
  return (
    <span
      className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${it.cls}`}
    >
      <Icon className={`h-3.5 w-3.5 ${"spin" in it && it.spin ? "animate-spin" : ""}`} />
      {it.label}
    </span>
  );
}

function formatDateTime(iso: string) {
  return formatDateTimeBR(iso, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
function formatTime(iso: string) {
  return formatTimeBR(iso);
}
function formatDuration(startIso: string, endIso: string) {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}
